import type { ProductSubscriptionInfoViewModel } from 'autogen/swagger/all';
import React, { useContext } from 'react';
import styled from 'styled-components';
import Card from 'components/generic/card';
import { ColorNeutralDarkGrey, FontWeightNormal } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import { BuyOnce } from 'autogen/translation-keys/trans-website-product-subscription';
import { PossibleAdditionalDeliveryCost } from 'autogen/translation-keys/trans-website-shared-productitem';
import { useTranslation } from 'react-i18next';
import { NotClubMatasPrice, PriceBefore, YourDiscount } from 'autogen/translation-keys/trans-website-product-priceinfo';
import { SubscriptionContext } from 'components/pages/PDP/components/MidSection/hooks/context';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { StyledCard, Headline, StyledRadio, Label, Price, Content, AdditionalLine, LineThrough } from './components';
import { inputValues } from '../shared/enums';
import { useProductStore } from 'components/pages/PDP/shared/productStore';

const PriceNote = styled.div`
    color: ${ColorNeutralDarkGrey};
    font-weight: ${FontWeightNormal};
`;

export default function BuyOnceCard(model: ProductSubscriptionInfoViewModel): JSX.Element {
    const {
        priceInfo: { price, onDiscount, priceLabel, priceBefore, discount },
        showPossibleAdditionalDeliveryCost
    } = model;
    const { subscriptionActive, setSubscriptionActive } = useContext(SubscriptionContext);
    const { setIsSubscription } = useProductStore();
    const { t } = useTranslation();
    const isClubMatasOnlyPrice = priceLabel?.type === 'ClubMatasOnlyPrice';
    const isMatasPlusPrice = priceLabel?.type === 'MatasPlusPrice';
    const priceBeforeLabel = isClubMatasOnlyPrice ? t(NotClubMatasPrice) : t(PriceBefore);

    function switchSelection(type: inputValues) {
        setSubscriptionActive(type === inputValues.subscription);
        setIsSubscription(type === inputValues.subscription);

        GA4Tracking({
            eventName: 'toggle_subscription',
            context: 'product_page',
            category: 'ecommerce',
            eventParams: [{ param_name: 'toggle', param_value: type === inputValues.subscription }]
        });
    }

    return (
        <StyledCard forwardedAs="label" $isActive={!subscriptionActive}>
            <Card.Content>
                <Headline $isActive={!subscriptionActive} forwardedAs="div">
                    <StyledRadio
                        name="ProductPaymentChoice"
                        value={inputValues.default}
                        onChange={() => switchSelection(inputValues.default)}
                        defaultChecked={!subscriptionActive}
                    />

                    <Label>{t(BuyOnce)}</Label>

                    <Price $isClubPrice={isClubMatasOnlyPrice || isMatasPlusPrice}>
                        {priceLabel?.text} {price}
                        {showPossibleAdditionalDeliveryCost && !subscriptionActive && (
                            <PriceNote>{t(PossibleAdditionalDeliveryCost)}</PriceNote>
                        )}
                    </Price>
                </Headline>

                {!subscriptionActive && onDiscount && (
                    <Content>
                        <AdditionalLine>
                            <Typography.Body as="span">{priceBeforeLabel}</Typography.Body>
                            <LineThrough forwardedAs="span">{priceBefore}</LineThrough>
                        </AdditionalLine>

                        <AdditionalLine>
                            <Typography.Body size="M" strong as="span">
                                {t(YourDiscount)}
                            </Typography.Body>
                            <Typography.Body size="M" strong as="span">
                                {discount}
                            </Typography.Body>
                        </AdditionalLine>
                    </Content>
                )}
            </Card.Content>
        </StyledCard>
    );
}
